import { apiFetchWithRetry } from '@/lib/apiFetch';

// --- Types ---

export interface PriorPeriodMetrics {
  totalInquiries: number;
  responseRate: number;
}

export interface OfferingTypeMetrics {
  totalImpressions: number;
  totalClicks: number;
  totalInquiries: number;
  clickThroughRate: number;
  responseRate: number;
  avgResponseTimeMinutes: number | null;
  prior: PriorPeriodMetrics | null;
}

export interface AnalyticsSummaryResponse {
  periodDays: number;
  oneToOne: OfferingTypeMetrics | null;
  group: OfferingTypeMetrics | null;
  unreadInquiryCount: number;
  tier: 'TOP_PERFORMER' | 'ESTABLISHED' | 'GROWING' | 'NEWCOMER';
  cohortSize: number;
  cohortMinimumMet: boolean;
  /**
   * F17 (AC#28): the teacher's paid subscription tier -- a DIFFERENT concept from
   * `tier` above (`PerformanceTier`, a performance band). Null means no paid
   * subscription. Never conflate the two -- see TeacherAnalyticsPage.tsx.
   */
  subscriptionTier: 'SILVER' | 'GOLD' | 'PLATINUM' | null;
  /**
   * F17 (AC#28): true only for GOLD/PLATINUM. When false, `benchmarks` and
   * `offeringBreakdown` on `AnalyticsInsightsResponse` come back as empty arrays --
   * gate UI on THIS flag, never on those arrays' length (an empty array is also a
   * legitimate state for an entitled teacher whose cohort is below minimum size).
   */
  benchmarksVisible: boolean;
  /**
   * ADR-140: true only for GOLD/PLATINUM. When false the server withholds `prior` on
   * `oneToOne`/`group` entirely -- the gate is server-side, so a client that forgets to check this
   * still cannot show a comparison.
   *
   * Gate UI on THIS flag, never on `prior == null`: an entitled teacher with no data in the
   * preceding window also gets null, and the two need different treatment if an upgrade prompt is
   * ever added.
   */
  periodComparisonVisible: boolean;
}

export interface BenchmarkResponse {
  subject: string;
  offeringType: string;
  cohortSize: number;
  cohortMinimumMet: boolean;
  yourResponseRate: number;
  cohortMedianResponseRate: number;
  cohortP75ResponseRate: number;
  yourClickThroughRate: number;
  cohortMedianClickThroughRate: number;
  cohortP75ClickThroughRate: number;
  yourInquiryVolume: number;
  cohortMedianInquiryVolume: number;
  cohortP75InquiryVolume: number;
}

export interface OfferingBreakdownResponse {
  offeringId: string;
  offeringTitle: string;
  offeringType: string;
  impressions: number;
  clicks: number;
  clickThroughRate: number;
}

export interface StudentEngagementResponse {
  uniqueStudents: number;
  repeatStudents: number;
  repeatRate: number;
}

export interface AnalyticsInsightsResponse {
  summary: AnalyticsSummaryResponse;
  benchmarks: BenchmarkResponse[];
  offeringBreakdown: OfferingBreakdownResponse[];
  studentEngagement: StudentEngagementResponse;
}

// --- API functions ---

export async function getAnalyticsSummary(accessToken: string): Promise<AnalyticsSummaryResponse> {
  const res = await apiFetchWithRetry('/api/v1/teacher/analytics/summary', {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch analytics summary: ${res.status}`);
  return res.json();
}

export async function getAnalyticsInsights(accessToken: string): Promise<AnalyticsInsightsResponse> {
  const res = await apiFetchWithRetry('/api/v1/teacher/analytics/insights', {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch analytics insights: ${res.status}`);
  return res.json();
}
